/**
 * DripConnect — customer review rail.
 *
 * Drives the horizontal screenshot carousel used by the homepage wall
 * (dc-reviews) and the product page wall (dc-product-proof). Both sections
 * render the same markup, so one element type covers both.
 *
 * Scrolling is hand-animated (requestAnimationFrame + easeInOutCubic) rather
 * than the native `scrollTo({behavior:'smooth'})`, which visibly fights
 * `scroll-snap-type` mid-flight and was the source of the laggy feel.
 *
 * The loop is seamless: the real cards are bracketed by one cloned copy on
 * each side. Advancing past the end (or before the start) scrolls into a
 * clone region that looks pixel-identical to the real one, and once the
 * animation settles there, the scroll position is silently re-baselined by
 * exactly one real-set width with no animation — invisible, since the card
 * on screen at that instant is visually identical before and after.
 *
 * Both the arrows and autoplay move exactly one card at a time — a
 * multi-card jump per click or per tick read as several reviews vanishing
 * at once rather than a deliberate step.
 * Motion is suppressed entirely under prefers-reduced-motion.
 */
(() => {
  if (customElements.get('dc-review-rail')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Accelerate then decelerate, so the motion reads as a deliberate glide
  // rather than a quick blip that's easy to miss.
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class DCReviewRail extends HTMLElement {
    connectedCallback() {
      if (this.dcReady) return;
      this.dcReady = true;

      this.track = this.querySelector('[data-dc-rail-track]');
      this.prev = this.querySelector('[data-dc-rail-prev]');
      this.next = this.querySelector('[data-dc-rail-next]');
      if (!this.track) return;

      this.autoplayMs = Number(this.dataset.autoplay || 0);
      this.paused = false;
      this.rafId = null;
      this.settleTimer = null;

      this.setUpLoop();

      this.onScroll = () => {
        this.throttledSyncEnds();
        clearTimeout(this.settleTimer);
        // Catches manual drags/swipes, which don't go through animateTo().
        this.settleTimer = setTimeout(() => this.resolveLoopPosition(), 150);
      };
      this.throttledSyncEnds = this.throttle(() => this.syncEnds());
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.prev?.addEventListener('click', () => this.page(-1));
      this.next?.addEventListener('click', () => this.page(1));

      // Any hover, focus or touch inside the rail means the shopper is reading
      // it, so autoplay should get out of the way until they leave.
      ['pointerenter', 'focusin', 'touchstart'].forEach((evt) =>
        this.addEventListener(evt, () => this.pause(), { passive: true })
      );
      ['pointerleave', 'focusout'].forEach((evt) =>
        this.addEventListener(evt, () => this.resume())
      );

      this.resizeObserver = new ResizeObserver(() => {
        this.teardownLoop();
        this.setUpLoop();
        this.syncEnds();
      });
      this.resizeObserver.observe(this.track);

      this.syncEnds();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      cancelAnimationFrame(this.rafId);
      clearTimeout(this.settleTimer);
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
    }

    /** Clone the real cards once on each side so advancing past an end
     *  scrolls into a lookalike region instead of dead-ending. */
    setUpLoop() {
      const realCards = [...this.track.children].filter((c) => !c.dataset.dcClone);
      this.loopEnabled = false;
      if (realCards.length < 2) return;
      if (this.track.scrollWidth <= this.track.clientWidth + 1) return;

      const anchor = realCards[0];
      realCards.forEach((card) => {
        const lead = card.cloneNode(true);
        lead.dataset.dcClone = 'true';
        lead.setAttribute('aria-hidden', 'true');
        lead.setAttribute('inert', '');
        this.track.insertBefore(lead, anchor);
      });
      realCards.forEach((card) => {
        const trail = card.cloneNode(true);
        trail.dataset.dcClone = 'true';
        trail.setAttribute('aria-hidden', 'true');
        trail.setAttribute('inert', '');
        this.track.appendChild(trail);
      });

      this.realSetWidth = this.cardWidth() * realCards.length;
      this.realStart = this.realSetWidth;
      this.realEnd = this.realStart + this.realSetWidth;
      this.loopEnabled = true;
      this.track.scrollLeft = this.realStart;
    }

    teardownLoop() {
      this.track.querySelectorAll('[data-dc-clone]').forEach((el) => el.remove());
      this.loopEnabled = false;
    }

    /** Silently re-baseline scrollLeft if we've drifted into a clone region.
     *  No-op, and invisible, when we're still inside the real region. */
    resolveLoopPosition() {
      if (!this.loopEnabled) return;
      const pos = this.track.scrollLeft;
      if (pos >= this.realEnd - 1) {
        this.track.scrollLeft = pos - this.realSetWidth;
      } else if (pos < this.realStart - 1) {
        this.track.scrollLeft = pos + this.realSetWidth;
      }
    }

    /** Width of a single card plus the gap after it. */
    cardWidth() {
      const card = this.track.querySelector('.dc-reviews__card');
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    /** Arrows and autoplay both move exactly one card at a time. */
    page(direction) {
      this.animateTo(this.track.scrollLeft + direction * this.cardWidth());
    }

    advance() {
      this.page(1);
    }

    animateTo(target, duration = 700) {
      cancelAnimationFrame(this.rafId);

      if (!this.loopEnabled) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        target = Math.max(0, Math.min(target, max));
      }
      if (REDUCED.matches) {
        this.track.scrollLeft = target;
        this.resolveLoopPosition();
        return;
      }

      const start = this.track.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 1) return;
      const startTime = performance.now();

      // scroll-snap-type actively fights repeated scrollLeft writes: Chromium
      // holds the position at the current snap point through several
      // intermediate writes, then jumps straight to the next snap point once
      // a threshold is crossed — which is exactly the "instant jump" bug,
      // confirmed by tracing raw scrollLeft writes during an animation. Turn
      // snapping off for the duration of the programmatic scroll, and only
      // let it back in once the animation (and the loop re-baseline) is done,
      // so manual drags/swipes keep their settle behavior untouched.
      this.track.style.scrollSnapType = 'none';

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        this.track.scrollLeft = start + distance * easeInOutCubic(progress);
        if (progress < 1) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.rafId = null;
          this.resolveLoopPosition();
          this.track.style.scrollSnapType = '';
        }
      };
      this.rafId = requestAnimationFrame(step);
    }

    syncEnds() {
      // With the loop bracketed by clones there's always somewhere to go,
      // so ends only read as "stuck" when looping is off entirely.
      const max = this.track.scrollWidth - this.track.clientWidth;
      const single = max <= 1;
      const atStart = !this.loopEnabled && this.track.scrollLeft <= 1;
      const atEnd = !this.loopEnabled && this.track.scrollLeft >= max - 1;

      this.classList.toggle('is-at-start', atStart);
      this.classList.toggle('is-at-end', atEnd);
      this.classList.toggle('is-static', single);
      if (this.prev) this.prev.disabled = single;
      if (this.next) this.next.disabled = single;
    }

    startAutoplay() {
      if (!this.autoplayMs || REDUCED.matches) return;
      this.stopAutoplay();
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        if (this.track.scrollWidth - this.track.clientWidth <= 1) return;
        this.advance();
      }, this.autoplayMs);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
    }

    resume() {
      this.paused = false;
    }

    throttle(fn) {
      let queued = false;
      return () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn();
        });
      };
    }
  }

  customElements.define('dc-review-rail', DCReviewRail);
})();
